import dayjs from 'dayjs';
import EventEmitter from 'eventemitter3';
import { $notificationStore, $profileStore } from '@lib/store';
import { LocalAuthData, LocalStorageUtils, PkceUtils, RoutePaths } from '.';
import { AuthApi, RestApiError } from '@shared/api';
import { CoreUtils } from './coreUtils';

const isDevelopment = process.env.NODE_ENV === 'development';

export class AuthUtils {
  static readonly isAuthEventName = 'isAuth';
  static readonly eventIsAuth = new EventEmitter<string, boolean>();
  static authDataCached: LocalAuthData | undefined = undefined;
  private static latestCallRefreshAuthDate = dayjs();
  private static readonly accessTokenMinutes = 10; //It's constant value for current project
  private static readonly publicPages = [RoutePaths.signup.onNavigate(), RoutePaths.success.onNavigate()];
  // PERSONAL
  static registerToken = '';
  static resendCodeToken = '';
  static verifyToken = '';
  static setToken = (key: 'registerToken' | 'resendCodeToken' | 'verifyToken', value?: string) => {
    if (!value) return;
    switch (key) {
      case 'registerToken':
        this.registerToken = value;
        break;
      case 'resendCodeToken':
        this.resendCodeToken = value;
        break;
      case 'verifyToken':
        this.verifyToken = value;
        break;
    }
  };

  static init = async () => {
    this.authDataCached = LocalStorageUtils.getAuthData();
    this.runLogOutTimer();
    this.runRefreshAuthTimer();
    await this.isUserBlocked();
    AuthUtils.updateRefreshAuthDate();
    //Update token when use mousemove
    window.addEventListener('mousemove', this.onCallbackDocumentMouseMove);
    window.addEventListener('keypress', this.onCallbackDocumentMouseMove);
  };

  static isAuth = () => this.authDataCached != null && this.isValidExpiresDate();

  static getLoginUrl = async (): Promise<string> => {
    const codeVerifier = PkceUtils.generateCodeVerifier();
    const codeChallenge = await PkceUtils.generateCodeChallenge(codeVerifier);
    PkceUtils.storeCodeVerifier(codeVerifier);

    const url = new URL(process.env.LOGIN_URL!);
    url.searchParams.set('code_challenge', codeChallenge);
    url.searchParams.set('code_challenge_method', 'S256');
    return url.toString();
  };

  static getPkceChallengeForAuthenticate = () => PkceUtils.getCodeVerifier();

  static clearPkceChallenge = () => PkceUtils.clearCodeVerifier();

  static redirectToLoginPage = async (options: { showNotification?: boolean; force?: boolean } = { showNotification: true }) => {
    const publicPages = [RoutePaths.signup.onNavigate(), RoutePaths.success.onNavigate()];
    const currentUrl = window.location.href.toLocaleLowerCase();
    if (!options.force && (currentUrl.includes(publicPages[0]) || currentUrl.includes(publicPages[1]))) return;

    let showNotification = true;
    if (options.showNotification === false) showNotification = false;
    else if (!this.authDataCached) showNotification = false;
    else if (dayjs.unix(this.authDataCached.expiresDate).add(this.accessTokenMinutes, 'minutes').unix() < dayjs().unix()) showNotification = false;
    showNotification && $notificationStore.events.setWarning('Your session timed out due to inactivity. Please log in again.');

    window.location.href = await this.getLoginUrl();
  };

  static updateAuthData = (authData: LocalAuthData) => {
    const dateNow = dayjs();
    authData.expiresDate = dateNow.add(this.accessTokenMinutes, 'minutes').unix();
    LocalStorageUtils.updateAuthData(authData);
    this.authDataCached = authData;
    this.runLogOutTimer();
    if (!this.timerRefreshAuth) this.runRefreshAuthTimer();
    this.eventIsAuth.emit(this.isAuthEventName, true);
  };

  static getTempTokenForLogIn = () => new URLSearchParams(window.location.search).get('code');

  static logout = (options: { showNotification?: boolean } = {}) => {
    if (isDevelopment) return;

    this.disableRefreshAuthTimer();
    this.disableLogOutTimer();
    LocalStorageUtils.clearAuthData();
    this.authDataCached = undefined;
    // this.eventIsAuth.emit(this.isAuthEventName, false);
    options.showNotification && $notificationStore.events.setWarning('Your session timed out due to inactivity. Please log in again.');
    void this.redirectToLoginPage({ showNotification: false });
  };

  static getUserId = () => $profileStore.store.getState().data?.id;

  private static updateRefreshAuthDate = () => {
    if (!this.isAuth()) return;
    this.latestCallRefreshAuthDate = dayjs();
  };

  private static refreshAuth = async () => {
    try {
      const authData = this.authDataCached;
      if (!authData) return;
      if (!this.isAuth()) return;

      if (!isDevelopment) {
        const now = dayjs();
        const expDate = this.latestCallRefreshAuthDate.add(70, 'seconds');
        if (expDate.valueOf() < now.valueOf()) return;
      }

      const result = await AuthApi.refreshToken({ refreshToken: authData.refreshToken });
      this.updateAuthData(result as LocalAuthData);
    } catch (error) {
      console.error('Error refreshing token:', error);
    }
  };

  private static isUserBlocked = async () => {
    if (!this.isAuth()) return;
    try {
      $profileStore.events.getProfile();
    } catch (e) {
      if (RestApiError.isRestApiError(e) && e.status === 401) this.logout();
      throw e;
    }
  };

  private static isValidExpiresDate = () => {
    if (!this.authDataCached) return false;
    const nowDate = dayjs();
    const expDate = dayjs.unix(this.authDataCached.expiresDate);
    const isValid = expDate > nowDate;
    return isValid;
  };

  private static parseAccessToken = (authData?: LocalAuthData) => {
    const data = authData || this.authDataCached;
    if (data == null) return undefined;
    const result = JSON.parse(atob(data.accessToken.split('.')[1]));
    return result;
  };

  //#region Refresh Timer
  private static readonly timeRefreshInterval = 60 * 1000; //1 min
  private static timerRefreshAuth: NodeJS.Timeout | null = null;
  private static runRefreshAuthTimer = () => {
    if (!this.isAuth()) return;

    this.disableRefreshAuthTimer();
    this.timerRefreshAuth = setInterval(async () => await this.refreshAuth(), this.timeRefreshInterval);
  };
  private static disableRefreshAuthTimer = () => {
    if (!this.timerRefreshAuth) return;
    clearInterval(this.timerRefreshAuth);
    this.timerRefreshAuth = null;
  };
  //#endregion

  //#region LogOut Timer
  private static timerLogOut: NodeJS.Timeout | null = null;

  private static runLogOutTimer = () => {
    const authData = this.authDataCached;
    if (!authData) return;

    if (!this.isValidExpiresDate()) {
      this.logout({ showNotification: true });
      return;
    }

    const expDate = dayjs.unix(authData.expiresDate);
    const timeLeft = expDate.valueOf() - dayjs().valueOf();

    this.disableLogOutTimer();
    this.timerLogOut = setTimeout(() => this.logout({ showNotification: true }), timeLeft);
  };

  private static disableLogOutTimer = () => {
    if (this.timerLogOut) clearTimeout(this.timerLogOut);
  };
  //#endregion

  private static onCallbackDocumentMouseMove = CoreUtils.debounce(() => AuthUtils.updateRefreshAuthDate(), 250);
}
